import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { AdminLayout } from './AdminDashboard';
import { LuStar } from 'react-icons/lu';

export default function AdminReviews() {
  const qc = useQueryClient();

  const { data: reviews } = useQuery({
    queryKey: ['admin-reviews-all'],
    queryFn: () => axios.get('/api/reviews/admin/all').then(r => r.data).catch(() => [])
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => axios.put(`/api/reviews/${id}`, data),
    onSuccess: () => { toast.success('Review updated'); qc.invalidateQueries(['admin-reviews-all']); }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => axios.delete(`/api/reviews/${id}`),
    onSuccess: () => { toast.success('Review deleted'); qc.invalidateQueries(['admin-reviews-all']); }
  });

  const toggle = (review, field) => updateMutation.mutate({ id: review._id, data: { [field]: !review[field] } });

  return (
    <>
      <Helmet><title>Reviews – Villa Tesori Admin</title></Helmet>
      <AdminLayout title="Reviews Management">
        <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
          {[
            { label: 'Total', count: reviews?.length || 0, color: '#e8f0f8' },
            { label: 'Pending', count: reviews?.filter(r => !r.isApproved).length || 0, color: '#fdf6e3' },
            { label: 'Approved', count: reviews?.filter(r => r.isApproved).length || 0, color: '#e8f4e8' },
          ].map(s => (
            <div key={s.label} style={{ background: s.color, padding: '16px 24px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.06)' }}>
              <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '2rem', color: 'var(--forest)' }}>{s.count}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{s.label}</div>
            </div>
          ))}
        </div>

        <div style={{ background: 'var(--white)', borderRadius: 8, border: '1px solid #e8e8e8', overflow: 'hidden' }}>
          {(reviews || []).length === 0 && (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>No reviews yet</div>
          )}
          {(reviews || []).map(r => (
            <div key={r._id} style={{ padding: '20px 24px', borderBottom: '1px solid #f5f5f5' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, marginRight: 24 }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontWeight: 500 }}>{r.guestName}</span>
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{r.location}</span>
                    <div style={{ display: 'flex', gap: 2 }}>
                      {[...Array(5)].map((_, i) => (
                        <LuStar 
                          key={i} 
                          size={14} 
                          style={{ 
                            fill: i < r.rating ? 'var(--gold)' : 'transparent',
                            color: i < r.rating ? 'var(--gold)' : '#ddd'
                          }} 
                        />
                      ))}
                    </div>
                  </div>
                  <div style={{ fontStyle: 'italic', color: 'var(--forest)', marginBottom: 4, fontWeight: 500 }}>"{r.title}"</div>
                  <p style={{ fontSize: '0.88rem', color: 'var(--text-muted)', lineHeight: 1.7 }}>{r.content.slice(0, 200)}{r.content.length > 200 ? '...' : ''}</p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 160 }}>
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: '0.7rem', background: r.isApproved ? '#e8f4e8' : '#fdf6e3', color: r.isApproved ? '#2d5a2d' : '#856404' }}>
                      {r.isApproved ? 'Approved' : 'Pending'}
                    </span>
                    {r.isFeautured && <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: '0.7rem', background: '#e8f0f8', color: '#1a4a8a' }}>Featured</span>}
                  </div>
                  <button onClick={() => toggle(r, 'isApproved')} style={{ background: r.isApproved ? '#fff0f0' : '#e8f4e8', border: '1px solid', borderColor: r.isApproved ? '#fdd' : '#c3e6cb', padding: '7px 12px', borderRadius: 4, cursor: 'pointer', fontSize: '0.78rem', color: r.isApproved ? '#c00' : '#2d5a2d' }}>
                    {r.isApproved ? 'Unapprove' : 'Approve'}
                  </button>
                  <button onClick={() => toggle(r, 'isFeautured')} style={{ background: '#f8f8ff', border: '1px solid #ddd', padding: '7px 12px', borderRadius: 4, cursor: 'pointer', fontSize: '0.78rem' }}>
                    {r.isFeautured ? 'Unfeature' : 'Feature'}
                  </button>
                  <button onClick={() => { if (window.confirm('Delete this review?')) deleteMutation.mutate(r._id); }} style={{ background: 'none', border: '1px solid #fdd', padding: '7px 12px', borderRadius: 4, cursor: 'pointer', fontSize: '0.78rem', color: '#c00' }}>
                    Delete
                  </button>
                </div>
              </div>
              <div style={{ marginTop: 8, fontSize: '0.75rem', color: '#bbb' }}>
                Submitted: {new Date(r.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} · Source: {r.source}
              </div>
            </div>
          ))}
        </div>
      </AdminLayout>
    </>
  );
}
